import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ConversationListItemDTO, ConversationChannel } from "@tw-pulse/shared";
import { CONVERSATION_CHANNELS, CONVERSATION_CHANNEL_LABELS } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";

// Derived from the shared enum, never re-typed. This list used to be a
// hand-written union of "sms" | "rcs" | "sandbox" and fell three channels
// behind the database, so WhatsApp, voice, and webhook conversations could
// not be filtered for at all.
type ChannelFilter = "" | ConversationChannel;
type StatusFilter = "" | "active" | "closed" | "handed_off";

/**
 * Read-only list of past conversations. Operators click a row to drop
 * into the per-conversation replay view.
 */
export function ConversationsListPage() {
  const [items, setItems] = useState<ConversationListItemDTO[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [channel, setChannel] = useState<ChannelFilter>("");
  const [status, setStatus] = useState<StatusFilter>("");

  async function load(reset: boolean) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.agentflow.listConversations({
        channel: channel === "" ? undefined : channel,
        status: status === "" ? undefined : status,
        cursor: reset ? undefined : (cursor ?? undefined),
        limit: 50,
      });
      setItems((prev) => (reset ? res.conversations : [...prev, ...res.conversations]));
      setCursor(res.nextCursor);
      setHasMore(Boolean(res.nextCursor));
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to load conversations");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, status]);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Conversations
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Conversation replay
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          Read-only audit view of every past conversation, including tool calls and results.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as ChannelFilter)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40"
        >
          <option value="">All channels</option>
          {CONVERSATION_CHANNELS.map((c) => (
            <option key={c} value={c}>
              {CONVERSATION_CHANNEL_LABELS[c]}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="handed_off">Handed off</option>
        </select>
      </div>

      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800/80 dark:bg-zinc-900/40">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
            <tr>
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Channel</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Turns</th>
              <th className="px-4 py-2.5">Last turn</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
            {items.map((c) => (
              <tr key={c.id} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
                <td className="px-4 py-3">
                  <Link
                    to={`/agentflow/conversations/${c.id}`}
                    className="font-mono text-[13px] text-brand-600 hover:text-brand-700 dark:text-brand-300"
                  >
                    {c.endCustomerRef}
                  </Link>
                </td>
                <td className="px-4 py-3 text-[12.5px] uppercase text-zinc-600 dark:text-zinc-400">
                  {c.channel}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-zinc-700 dark:text-zinc-300">
                  {c.status}
                  {c.completed && (
                    <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      complete
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[12.5px]">{c.turnCount}</td>
                <td className="px-4 py-3 text-[12px] text-zinc-500">
                  {new Date(c.lastTurnAt).toLocaleString()}
                </td>
              </tr>
            ))}
            {items.length === 0 && !busy && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-[12.5px] text-zinc-500">
                  No conversations yet in this scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void load(false)}
            disabled={busy}
            className="text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300"
          >
            {busy ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
